import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import moment from 'moment';
import { encodeId } from '@app/helpers/base64url';
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import { Button } from '@app/components';
import {
  getBatchUploadById,
  retryBatchItem,
  BatchUploadDetail,
  BatchUploadItem,
} from '@app/services/studentAutoMarking';

import './StudentAutoMarking.css';

const tableStyles: TableStyles = {
  head: {
    style: { color: '#5E5873', fontSize: '13px', fontWeight: 900 },
  },
  headCells: {
    style: { borderColor: '#E9ECEF', textTransform: 'uppercase', letterSpacing: '1px' },
  },
  headRow: {
    style: { backgroundColor: '#F7F7F8', minHeight: '44px', fontWeight: 500 },
  },
};

const POLL_INTERVAL = 3000; // 3 seconds

const AutoMarkBatchProgress = () => {
  const isMounted = useRef(true);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const studentId = useDecodedParam('studentId', '/students');
  const batchId = useDecodedParam('batchId', `/student/${encodeId(studentId)}/auto-marking`);

  const [batch, setBatch] = useState<BatchUploadDetail | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [retryingItems, setRetryingItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (batchId) {
      fetchBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const fetchBatch = async () => {
    try {
      const response = await getBatchUploadById(batchId);
      if (!isMounted.current) return;

      if (response?.success) {
        const batchData = response.data;
        
        // Empty data object means batch not found
        if (!batchData || Object.keys(batchData).length === 0) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setBatch(batchData);
        setLoading(false);
        setNotFound(false);

        // Continue polling if status is not terminal
        const isTerminal = ['completed', 'completed_with_errors', 'failed'].includes(
          batchData.status
        );
        if (!isTerminal && isMounted.current) {
          pollTimerRef.current = setTimeout(fetchBatch, POLL_INTERVAL);
        }
      } else {
        if (isMounted.current) {
          toast.error(response?.message || 'Failed to load batch details');
          setLoading(false);
        }
      }
    } catch (error) {
      if (isMounted.current) {
        toast.error('Error loading batch details');
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      queued: 'badge-secondary',
      processing: 'badge-warning',
      done: 'badge-success',
      failed: 'badge-danger',
      invalid: 'badge-danger',
      completed: 'badge-success',
      completed_with_errors: 'badge-warning',
    };
    return badges[status] || 'badge-secondary';
  };

  const getStatusText = (status: string) => {
    const text: Record<string, string> = {
      queued: 'Queued',
      processing: 'Processing',
      done: 'Done',
      failed: 'Failed',
      invalid: 'Invalid',
      completed: 'Completed',
      completed_with_errors: 'Completed with Errors',
    };
    return text[status] || status;
  };

  const handleRetryItem = async (itemId: number) => {
    try {
      setRetryingItems((prev) => new Set(prev).add(itemId));
      const resp = await retryBatchItem(batchId, itemId);
      if (resp?.success) {
        toast.success('Item queued for retry');
        // Refresh batch data immediately
        fetchBatch();
      } else {
        toast.error(resp?.message || 'Failed to retry item');
      }
    } catch (error) {
      toast.error('Error retrying item');
    } finally {
      setRetryingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const columns = [
    {
      name: 'File Name',
      selector: (row: BatchUploadItem) => row.original_name,
      sortable: true,
      wrap: true,
      minWidth: '200px',
    },
    {
      name: 'Module / Worksheet',
      // Format: "<Module> - <Worksheet>", e.g. "DU3a - 45"
      cell: (row: BatchUploadItem) =>
        row.module_name || row.worksheet_number ? (
          <span className="font-weight-bold">
            {[row.module_name, row.worksheet_number].filter(Boolean).join(' - ')}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
      sortable: false,
      minWidth: '220px',
    },
    {
      name: 'Status',
      cell: (row: BatchUploadItem) => (
        <span className={`badge ${getStatusBadge(row.status)}`}>
          {getStatusText(row.status)}
        </span>
      ),
      sortable: true,
      width: '130px',
    },
    {
      name: 'Score',
      cell: (row: BatchUploadItem) => {
        if (row.status === 'done' && row.total_marks !== null && row.max_marks !== null) {
          return (
            <div>
              <div className="font-weight-bold">
                {row.total_marks}/{row.max_marks}
              </div>
              {row.percentage !== null && (
                <small className="text-muted">{row.percentage}%</small>
              )}
            </div>
          );
        }
        return <span className="text-muted">—</span>;
      },
      sortable: false,
      width: '100px',
    },
    {
      name: 'Error / Details',
      cell: (row: BatchUploadItem) => {
        const isRetrying = retryingItems.has(row.id);
        
        if (row.status === 'failed') {
          return (
            <div>
              {row.error && (
                <div className="text-danger mb-2" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                  {row.error}
                </div>
              )}
              <button
                className="btn btn-sm btn-warning"
                onClick={() => handleRetryItem(row.id)}
                disabled={isRetrying}
                style={{ minWidth: '100px' }}
              >
                {isRetrying ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                    Retrying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-redo mr-1"></i>
                    Retry
                  </>
                )}
              </button>
            </div>
          );
        }
        
        if (row.status === 'done' && row.evaluation_id) {
          return (
            <Link
              to={`/student/${encodeId(studentId)}/auto-marking/${encodeId(row.evaluation_id)}`}
              state={{ studentEncodedId: encodeId(studentId) }}
              className="btn btn-sm btn-primary"
              style={{ minWidth: '100px' }}
            >
              <i className="fas fa-eye mr-1"></i>
              View Details
            </Link>
          );
        }

        // Invalid uploads: show the reason returned by the API (no retry, same file would fail again)
        if (row.status === 'invalid' && row.error) {
          return (
            <div className="text-danger" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
              {row.error}
            </div>
          );
        }
        
        return <span className="text-muted">—</span>;
      },
      sortable: false,
      minWidth: '200px',
      wrap: true,
    },
  ];

  if (isLoading) {
    return (
      <>
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Batch Upload Progress</h1>
              </div>
            </div>
          </div>
        </section>
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">Loading batch details...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Batch Not Found</h1>
              </div>
            </div>
          </div>
        </section>
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
                <h4>Batch Not Found</h4>
                <p className="text-muted">
                  The batch you're looking for doesn't exist or has been removed.
                </p>
                <Button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate(`/student/${encodeId(studentId)}/auto-marking`)}
                >
                  Back to Auto-Marking List
                </Button>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!batch) return null;

  const progressPercentage = batch.total > 0 ? Math.round((batch.done / batch.total) * 100) : 0;
  const isTerminal = ['completed', 'completed_with_errors', 'failed'].includes(batch.status);

  return (
    <>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Batch Upload Progress</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/students">Students</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/student/${encodeId(studentId)}/auto-marking`}>Auto-Marking</Link>
                </li>
                <li className="breadcrumb-item active">Batch Progress</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {/* Status Summary Card */}
          <div className="card card-outline card-primary">
            <div className="card-header">
              <h3 className="card-title">
                Batch #{batch.id} - <span className={`badge ${getStatusBadge(batch.status)}`}>
                  {getStatusText(batch.status)}
                </span>
              </h3>
              <div className="card-tools">
                <Button
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate(`/student/${encodeId(studentId)}/auto-marking`)}
                >
                  <i className="fas fa-arrow-left mr-1"></i> Back to List
                </Button>
              </div>
            </div>
            <div className="card-body py-3">
              {batch.status === 'failed' && batch.error && (
                <div className="alert alert-danger mb-3" style={{ padding: '10px 15px' }}>
                  <strong><i className="icon fas fa-ban mr-1"></i> Batch Failed:</strong> {batch.error}
                </div>
              )}

              {/* Inline Stats */}
              <div className="row mb-3">
                <div className="col-md-3 col-6 mb-2">
                  <div className="d-flex align-items-center p-2" style={{ backgroundColor: '#e7f5f9', borderRadius: '6px', border: '1px solid #bee5eb' }}>
                    <div className="mr-3" style={{ fontSize: '24px', color: '#17a2b8', width: '32px', textAlign: 'center' }}>
                      <i className="fas fa-images"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, marginBottom: '4px' }}>
                        {batch.total}
                      </div>
                      <small className="text-muted">Total Worksheets</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-2">
                  <div className="d-flex align-items-center p-2" style={{ backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #c3e6cb' }}>
                    <div className="mr-3" style={{ fontSize: '24px', color: '#28a745', width: '32px', textAlign: 'center' }}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, marginBottom: '4px' }}>
                        {batch.done}
                      </div>
                      <small className="text-muted">Processed</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-2">
                  <div className="d-flex align-items-center p-2" style={{ backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeaa7' }}>
                    <div className="mr-3" style={{ fontSize: '24px', color: '#ffc107', width: '32px', textAlign: 'center' }}>
                      <i className="fas fa-clock"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, marginBottom: '4px' }}>
                        {batch.total - batch.done}
                      </div>
                      <small className="text-muted">Remaining</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-2">
                  <div className="d-flex align-items-center p-2" style={{ backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #b3d9ff' }}>
                    <div className="mr-3" style={{ fontSize: '24px', color: '#007bff', width: '32px', textAlign: 'center' }}>
                      <i className="fas fa-chart-pie"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, marginBottom: '4px' }}>
                        {progressPercentage}%
                      </div>
                      <small className="text-muted">Progress</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              {!isTerminal && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted font-weight-bold">Processing Status</small>
                    <small className="text-muted">{batch.done} of {batch.total} completed</small>
                  </div>
                  <div className="progress" style={{ height: '24px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                      role="progressbar"
                      style={{ width: `${progressPercentage}%`, minWidth: progressPercentage > 0 ? '40px' : '0' }}
                      aria-valuenow={progressPercentage}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      {progressPercentage > 0 && (
                        <strong style={{ fontSize: '13px' }}>{progressPercentage}%</strong>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="row" style={{ fontSize: '13px' }}>
                <div className="col-sm-6">
                  <span className="text-muted">
                    <i className="fas fa-clock mr-1"></i>
                    Created: {moment(batch.created_at).format('MMM DD, YYYY h:mm A')}
                  </span>
                </div>
                <div className="col-sm-6 text-sm-right">
                  <span className="text-muted">
                    <i className="fas fa-sync mr-1"></i>
                    Last Updated: {batch.updated_at ? moment(batch.updated_at).format('MMM DD, YYYY h:mm A') : 'Not yet processed'}
                  </span>
                </div>
              </div>

              {!isTerminal && (
                <div className="alert alert-info mt-3 mb-0" style={{ padding: '8px 12px', fontSize: '13px' }}>
                  <i className="fas fa-info-circle mr-2"></i>
                  This page updates automatically every {POLL_INTERVAL / 1000} seconds while processing.
                </div>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="card card-outline card-info">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-list mr-2"></i>
                Uploaded Worksheets ({batch.items.length})
              </h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={batch.items}
                customStyles={tableStyles}
                pagination
                paginationPerPage={50}
                paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                highlightOnHover
                pointerOnHover={false}
                striped
                responsive
                noDataComponent={
                  <div className="text-center py-5">
                    <i className="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                    <p className="text-muted mb-0">No items in this batch</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AutoMarkBatchProgress;
